import { useEffect, useState } from "react";
import { Send, Inbox, X, Reply } from "lucide-react";
import { api } from "../lib/api";
import { toast } from "sonner";
import { useRealtime } from "../context/RealtimeContext";

export default function MessagingShared({ allowedRoles = ["padre", "profesor"] }) {
    const [tab, setTab] = useState("inbox");
    const [inbox, setInbox] = useState([]);
    const [sentMsgs, setSentMsgs] = useState([]);
    const [composing, setComposing] = useState(false);
    const [replyTo, setReplyTo] = useState(null);
    const [openMsg, setOpenMsg] = useState(null);
    const { lastMessage, markRead, markAllRead } = useRealtime();

    async function load() {
        const [a, b] = await Promise.all([api.get("/messages/inbox"), api.get("/messages/sent")]);
        setInbox(a.data || []);
        setSentMsgs(b.data || []);
    }
    useEffect(() => { load(); }, []);

    useEffect(() => {
        if (!lastMessage) return;
        setInbox((prev) => {
            if (prev.find((m) => m.id === lastMessage.id)) return prev;
            return [lastMessage, ...prev];
        });
        toast.message(`Nuevo mensaje de ${lastMessage.sender_name}`, {
            description: lastMessage.subject,
        });
    }, [lastMessage]);

    useEffect(() => {
        if (tab !== "inbox") return;
        const hasUnread = inbox.some((m) => !m.read);
        if (hasUnread) {
            const t = setTimeout(() => {
                markAllRead();
                setInbox((prev) => prev.map((m) => ({ ...m, read: true })));
            }, 1200);
            return () => clearTimeout(t);
        }
    }, [tab, inbox, markAllRead]);

    function openMessage(m) {
        if (!m.read) {
            markRead(m.id);
            setInbox((prev) => prev.map((x) => (x.id === m.id ? { ...x, read: true } : x)));
        }
        setOpenMsg(m);
    }

    return (
        <div className="p-6 md:p-10 fade-in" data-testid="mensajeria-page">
            <div className="flex items-center justify-between mb-6">
                <h1 className="text-3xl font-semibold">Mensajería</h1>
                <button
                    onClick={() => { setReplyTo(null); setComposing(true); }}
                    className="edu-btn-primary px-4 py-2.5 text-sm inline-flex items-center gap-2"
                    data-testid="compose-button"
                >
                    <Send size={14} /> Nuevo mensaje
                </button>
            </div>

            <div className="flex gap-2 mb-5" role="tablist">
                <Tab active={tab === "inbox"} onClick={() => setTab("inbox")} icon={Inbox} label="Recibidos" badge={inbox.filter((m) => !m.read).length} />
                <Tab active={tab === "sent"} onClick={() => setTab("sent")} icon={Send} label="Enviados" />
            </div>

            <div className="space-y-3">
                {(tab === "inbox" ? inbox : sentMsgs).length === 0 && (
                    <p className="text-edu-muted">Sin mensajes.</p>
                )}
                {(tab === "inbox" ? inbox : sentMsgs).map((m) => (
                    <div
                        key={m.id}
                        className={`w-full text-left edu-card p-5 transition relative ${tab === "inbox" && !m.read ? "border-l-4 border-l-edu-gold" : ""}`}
                        data-testid={`message-${m.id}`}
                    >
                        {tab === "inbox" && !m.read && (
                            <span className="absolute top-3 right-3 w-2.5 h-2.5 rounded-full bg-edu-gold" />
                        )}
                        <div className="flex items-center justify-between mb-1 pr-6">
                            <h3 className={`font-semibold ${!m.read && tab === "inbox" ? "text-edu-navy" : ""}`}>{m.subject}</h3>
                            <span className="text-xs text-edu-muted">{(m.created_at || "").slice(0, 10)}</span>
                        </div>
                        <p className="text-xs text-edu-muted mb-2">
                            {tab === "inbox" ? `De: ${m.sender_name} (${m.sender_role})` : `Para: ${m.recipient_name} (${m.recipient_role})`}
                        </p>
                        <p className="text-sm text-edu-body whitespace-pre-line mb-3">{m.body}</p>
                        {tab === "inbox" && (
                            <button
                                onClick={() => { openMessage(m); setReplyTo(m); setComposing(true); }}
                                className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs rounded-lg border border-edu hover:bg-edu-soft text-edu-navy"
                            >
                                <Reply size={12} /> Responder
                            </button>
                        )}
                    </div>
                ))}
            </div>

            {composing && (
                <ComposeModal
                    allowedRoles={allowedRoles}
                    replyTo={replyTo}
                    onClose={() => { setComposing(false); setReplyTo(null); }}
                    onSent={() => { setComposing(false); setReplyTo(null); load(); }}
                />
            )}
        </div>
    );
}

function Tab({ active, onClick, icon: Icon, label, badge }) {
    return (
        <button
            onClick={onClick}
            role="tab"
            aria-selected={active}
            className={`px-4 py-2 rounded-xl text-sm inline-flex items-center gap-2 ${active ? "bg-edu-navy text-white" : "bg-edu-card border border-edu hover:bg-gray-50"}`}
        >
            <Icon size={14} /> {label}
            {!!badge && (
                <span className="bg-edu-gold text-[#0A0A0A] text-[10px] font-bold rounded-full px-2 py-0.5 min-w-[20px] text-center">
                    {badge}
                </span>
            )}
        </button>
    );
}

function ComposeModal({ allowedRoles, onClose, onSent, replyTo }) {
    const [role, setRole] = useState(replyTo?.sender_role || allowedRoles[0]);
    const [users, setUsers] = useState([]);
    const [recipientId, setRecipientId] = useState(replyTo?.sender_id || "");
    const [subject, setSubject] = useState(replyTo ? `Re: ${replyTo.subject}` : "");
    const [body, setBody] = useState("");
    const [sending, setSending] = useState(false);

    useEffect(() => {
        api.get("/users", { params: { role } }).then((r) => setUsers(r.data || []));
    }, [role]);

    // Si es reply y ya cargaron los usuarios, setear el recipientId
    useEffect(() => {
        if (replyTo && users.length > 0) {
            setRecipientId(replyTo.sender_id || "");
        }
    }, [users, replyTo]);

    async function send() {
        setSending(true);
        try {
            await api.post("/messages", { recipient_id: recipientId, subject, body });
            toast.success("Mensaje enviado");
            onSent();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Error");
        } finally {
            setSending(false);
        }
    }

    return (
        <div
            className="fixed inset-0 w-screen h-screen bg-black/50 z-[99999] flex items-center justify-center p-4"
            role="dialog"
            aria-modal="true"
            onClick={onClose}
        >
            <div
                className="w-full max-w-lg bg-white p-6 rounded-2xl shadow-2xl max-h-[85vh] overflow-y-auto"
                onClick={(e) => e.stopPropagation()}
            >
                <div className="flex items-center justify-between mb-4">
                    <h3 className="text-xl font-bold text-edu-navy">
                        {replyTo ? "Responder mensaje" : "Nuevo mensaje"}
                    </h3>
                    <button onClick={onClose} className="text-gray-400 hover:text-gray-600 p-1">
                        <X size={20} />
                    </button>
                </div>

                {replyTo && (
                    <div className="mb-4 p-3 bg-gray-50 rounded-xl text-xs text-edu-muted border border-edu">
                        <p className="font-semibold text-edu-navy mb-1">Respondiendo a: {replyTo.sender_name}</p>
                        <p className="italic">"{replyTo.body.slice(0, 100)}{replyTo.body.length > 100 ? "…" : ""}"</p>
                    </div>
                )}

                <div className="space-y-4">
                    {!replyTo && (
                        <div className="grid grid-cols-2 gap-3">
                            <select
                                value={role}
                                onChange={(e) => { setRole(e.target.value); setRecipientId(""); }}
                                className="edu-input"
                                data-testid="recipient-role"
                            >
                                {allowedRoles.map((r) => (
                                    <option key={r} value={r}>
                                        {r === "padre" ? "Padres" : r === "profesor" ? "Profesores" : r === "psicologo" ? "Psicólogos" : r === "administrativo" ? "Administrativos" : r}
                                    </option>
                                ))}
                            </select>
                            <select
                                value={recipientId}
                                onChange={(e) => setRecipientId(e.target.value)}
                                className="edu-input"
                                data-testid="recipient-user"
                            >
                                <option value="">Seleccionar destinatario…</option>
                                {users.map((u) => (
                                    <option key={u.id} value={u.id}>{u.name}</option>
                                ))}
                            </select>
                        </div>
                    )}

                    {replyTo && (
                        <div className="edu-input w-full bg-gray-50 text-edu-muted text-sm py-2 px-3 rounded-xl">
                            Para: <strong>{replyTo.sender_name}</strong>
                        </div>
                    )}

                    <input
                        placeholder="Asunto"
                        value={subject}
                        onChange={(e) => setSubject(e.target.value)}
                        className="edu-input w-full"
                        data-testid="message-subject"
                    />
                    <textarea
                        placeholder="Mensaje…"
                        value={body}
                        onChange={(e) => setBody(e.target.value)}
                        rows={5}
                        className="edu-input w-full"
                        data-testid="message-body"
                    />
                    <div className="flex justify-end gap-3 pt-2">
                        <button onClick={onClose} className="px-4 py-2 text-sm rounded-xl border border-edu">Cancelar</button>
                        <button
                            onClick={send}
                            disabled={!recipientId || !subject || !body || sending}
                            className="edu-btn-primary px-4 py-2 text-sm"
                            data-testid="send-message"
                        >
                            {sending ? "Enviando…" : "Enviar"}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
}
